import { useMemo } from 'react';

import { getPath } from '~/src/common/components/Navigation/utils';
import { useElementsObserver } from '~/src/common/hooks/useElementsObserver';
import { useGetCategory, useNavigation } from '~/src/queries/api-ecom/generated/api-ecom';

export const useCategoryMenu = (slug: string) => {
  // Surveillance de la sous-catégorie la plus "visible"
  const { data: category } = useGetCategory(slug);
  const [visibleElementID] = useElementsObserver({
    wrapper: category?.id,
    config: { rootMargin: '-20% 0px -60% 0px' },
  });

  // Chargement de la navigation et calcul du chemin
  const { data: navigation } = useNavigation();
  const path = useMemo(() => {
    if (!navigation || !slug) return null;
    return getPath(navigation, slug);
  }, [navigation, slug]);

  // Les IDs des éléments du chemin en cours
  const familyID = path && 'family' in path ? path.family.item.id : null;
  const categoryID = path && 'category' in path ? path.category.item.id : null;
  const subcategoryID = path && 'subcategory' in path ? path.subcategory.item.id : null;
  const recipecategoryID = path && 'recipecategory' in path ? path.recipecategory.item.id : null;
  const recipesubcategoryID =
    path && 'recipesubcategory' in path ? path.recipesubcategory.item.id : null;

  // Regroupement des IDs actifs par type
  const activeIDs = [
    familyID,
    categoryID ?? recipecategoryID,
    subcategoryID ?? recipesubcategoryID ?? visibleElementID,
  ].filter(Boolean);

  // Pour tester si un ID donné est considéré comme actif
  const isActiveID = (id: string) => activeIDs.includes(id);

  // Tant que la navigation n'est pas disponible, on retourne `null`
  if (navigation == null) return {};

  // Le type de menu devant être affiché dépend du type du page en cours
  // - `classic` → les catégories présentes dans le méga-menu
  // - `recipes` → la catégorie recette ou ses sous-catégories
  // - `unknown` → les autres catégories, normalement inaccessibles
  const mode = familyID ? 'classic' : recipecategoryID ? 'recipes' : 'unknown';

  return { mode, navigation, isActiveID };
};
